import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import hitSound from "../../../Assets/final new sound/correct-2-46134.mp3";
import incorrectSound from "../../../Assets/final new sound/invalid-selection-39351.mp3";
import { server_url } from "../../../Config/API";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import AuthUser from "../../../Hooks/authUser";
import { getUserHook } from "../../../Hooks/getUserHook";
import GameStatsIndicator from "../GameStatsIndicator";

const BlueRedTheShooter = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);

  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const hitAudioRef = useRef(null);
  const incorrectAudioRef = useRef(null);
  if (typeof window !== "undefined") {
    if (!hitAudioRef.current) hitAudioRef.current = new Audio(hitSound);
    if (!incorrectAudioRef.current) incorrectAudioRef.current = new Audio(incorrectSound);
  }

  const [theShooter, setTheShooter] = useState([]);
  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;
  const previousStateRef = useRef(null);

  const scoreRef = useRef(score);
  const timeLeftRef = useRef(timeLeft);
  const gameOverRef = useRef(gameOver);

  scoreRef.current = score;
  timeLeftRef.current = timeLeft;
  gameOverRef.current = gameOver;

  // Red blue settings
  const [user, setUser] = useState([]);
  const blueRef = useRef(100);
  const redRef = useRef(100);

  useEffect(() => {
    getUserHook(userInfo?._id, setUser);
  }, []);

  useEffect(() => {
    if (user) {
      redRef.current = user?.redOpacity ?? 100;
      blueRef.current = user?.blueOpacity ?? 100;
    }
  }, [user]);

  // Projectiles and enemies lists
  const projectilesRef = useRef([]);
  const enemiesRef = useRef([]);
  const animationFrameIdRef = useRef(null);
  const spawnIntervalIdRef = useRef(null);

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/theShooter/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=The+Shooter+Binocular`
      );
      const data = await response.json();
      setTheShooter(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching blue-red shooter data:", error);
    }
  };

  useEffect(() => {
    if (theShooter?.length) {
      setTimeLeft(theShooter[0]?.remainingTime);
      setScore(theShooter[0]?.score);
    }
  }, [theShooter]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/theShooter/updateTheShooter/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeLeftRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/theShooter/addTheShooter`,
        {
          gameName: "The Shooter Binocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeLeftRef.current,
        },
        "The Shooter score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();

    const canvas = canvasRef.current;
    const c = canvas.getContext("2d");

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const x = canvas.width / 2;
    const y = canvas.height / 2;
    const playerRadius = 25;
    const projectileRadius = 10;
    const enemyRadius = 20;

    class Player {
      constructor(x, y) {
        this.x = x;
        this.y = y;
      }
      draw() {
        c.beginPath();
        c.arc(this.x, this.y, playerRadius, 0, Math.PI * 2, false);
        c.fillStyle = "yellow";
        c.fill();
      }
    }

    class Projectile {
      constructor(x, y, velocity) {
        this.x = x;
        this.y = y;
        this.velocity = velocity;
      }
      draw() {
        c.beginPath();
        c.arc(this.x, this.y, projectileRadius, 0, Math.PI * 2, false);
        c.fillStyle = "yellow";
        c.fill();
      }
      update() {
        this.draw();
        this.x += this.velocity.x;
        this.y += this.velocity.y;
      }
    }

    class Enemy {
      constructor(x, y, velocity) {
        this.x = x;
        this.y = y;
        this.velocity = velocity;
        this.colorType = Math.random() < 0.5 ? "red" : "blue";
      }
      draw() {
        c.beginPath();
        c.arc(this.x, this.y, enemyRadius, 0, Math.PI * 2, false);
        const opacity = this.colorType === "red" ? redRef.current / 100 : blueRef.current / 100;
        c.fillStyle = this.colorType === "red" ? `rgba(255, 0, 0, ${opacity})` : `rgba(0, 0, 255, ${opacity})`;
        c.fill();
      }
      update() {
        this.draw();
        this.x += this.velocity.x;
        this.y += this.velocity.y;
      }
    }

    const player = new Player(x, y);

    function spawnEnemies() {
      const radius = enemyRadius;
      let ex, ey;
      if (Math.random() < 0.5) {
        ex = Math.random() < 0.5 ? 0 - radius : canvas.width + radius;
        ey = Math.random() * canvas.height;
      } else {
        ex = Math.random() * canvas.width;
        ey = Math.random() < 0.5 ? 0 - radius : canvas.height + radius;
      }
      const angle = Math.atan2(player.y - ey, player.x - ex);
      const speed = 1.5;
      const velocity = {
        x: Math.cos(angle) * speed,
        y: Math.sin(angle) * speed,
      };
      enemiesRef.current.push(new Enemy(ex, ey, velocity));
    }

    spawnIntervalIdRef.current = setInterval(spawnEnemies, 1500);

    function animate() {
      if (gameOverRef.current) return;
      animationFrameIdRef.current = requestAnimationFrame(animate);
      c.clearRect(0, 0, canvas.width, canvas.height);
      player.draw();

      const projectiles = projectilesRef.current;
      const enemies = enemiesRef.current;

      // Update projectiles
      for (let pIndex = projectiles.length - 1; pIndex >= 0; pIndex--) {
        const projectile = projectiles[pIndex];
        projectile.update();

        if (
          projectile.x + projectileRadius < 0 ||
          projectile.x - projectileRadius > canvas.width ||
          projectile.y + projectileRadius < 0 ||
          projectile.y - projectileRadius > canvas.height
        ) {
          projectiles.splice(pIndex, 1);
        }
      }

      // Update enemies
      for (let eIndex = enemies.length - 1; eIndex >= 0; eIndex--) {
        const enemy = enemies[eIndex];
        enemy.update();

        // Clean up off-screen enemies to prevent memory leaks and lag
        if (
          enemy.x + enemyRadius < -100 ||
          enemy.x - enemyRadius > canvas.width + 100 ||
          enemy.y + enemyRadius < -100 ||
          enemy.y - enemyRadius > canvas.height + 100
        ) {
          enemies.splice(eIndex, 1);
          continue;
        }

        const distanceToPlayer = Math.hypot(player.x - enemy.x, player.y - enemy.y);
        if (distanceToPlayer - enemyRadius - playerRadius < 1) {
          // Play incorrect audio instead of stopping the game
          if (incorrectAudioRef.current) {
            incorrectAudioRef.current.currentTime = 0;
            incorrectAudioRef.current.play().catch(e => console.log(e));
          }
          enemies.splice(eIndex, 1);
          setScore((prev) => Math.max(0, prev - 1));
          continue;
        }

        // Collision with projectiles
        for (let pIndex = projectiles.length - 1; pIndex >= 0; pIndex--) {
          const projectile = projectiles[pIndex];
          const distanceToProjectile = Math.hypot(
            projectile.x - enemy.x,
            projectile.y - enemy.y
          );

          if (distanceToProjectile - enemyRadius - projectileRadius < 1) {
            enemies.splice(eIndex, 1);
            projectiles.splice(pIndex, 1);
            setScore((prev) => prev + 1);

            if (hitAudioRef.current) {
              hitAudioRef.current.currentTime = 0;
              hitAudioRef.current.play().catch(e => console.log(e));
            }
            break;
          }
        }
      }
    }

    animate();

    const handleClick = (event) => {
      if (gameOverRef.current) return;
      const angle = Math.atan2(event.clientY - y, event.clientX - x + 5);
      const speed = 25;
      const velocity = {
        x: Math.cos(angle) * speed,
        y: Math.sin(angle) * speed,
      };
      projectilesRef.current.push(new Projectile(x, y, velocity));
    };

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
      clearInterval(spawnIntervalIdRef.current);
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
    };
  }, []);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          setGameOver(true);
          insertFunctionRef.current();
          navigate(-1);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [navigate]);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (event.keyCode === 27) {
        insertFunctionRef.current();
        await navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFullScreen, navigate]);

  return (
    <div className=" inset-0 fixed bg-black ">
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
      <canvas ref={canvasRef} />
    </div>
  );
};

export default BlueRedTheShooter;
